import { useApolloClient } from '@apollo/react-hooks';
import { useServiceProviderInfo } from './useInvoicePaymentAccountInfo';
import useInvoicePaymentAuthQuery from './useInvoicePaymentAuthQuery';
import {
  InvoiceDetailsList,
  InvoicePaymentConfirmationList,
  InvoicePaymentInputList,
  MutationInvoiceMakePaymentArgs,
  PaymentInputList,
} from '../../__generated__/pge-types';
import { GET_INVOICE_LIST_QUERY } from './useInvoiceList';
import useUtil from './useUtil';
import { useTranslation } from '../useTranslation';
import useWrapWithLoader from '../useWrapWithLoading';
import useFormState from '../useFormState';
import { useEffect, useState } from 'react';
import { InvoicePaymentState } from '../../components/invoice-payment-portal/invoicePayment.types';
import { OneTimePayConfigData } from '../../components/payment-form/oneTimePayment.types';
import { navigate } from 'gatsby';
import { getInvoiceUrl } from '../../components/invoice-payment-portal/utils/navigation.util';
import routes from '../../routes';
import gql from 'graphql-tag';
import useInvoicePaymentAuth from './useInvoicePaymentAuth';

export const submitPaymentMutation = gql`
  mutation invoiceMakePayment($payload: InvoicePaymentInputList!) {
    invoiceMakePayment(payload: $payload) {
      paymentConfirmation {
        cardType
        confirmationId
        digitalWalletEmail        
        isSuccess
        lastFourDigitsCardNumber
        paymentAmount
        paymentMethod
        errorReason
        errorMessage
        invoiceId
        paymentFeeAmount
      }
    }
  }
`;

export const GET_PAYMENT_PROFILE_FEE = gql`
  query GetPaymentFee($payload: PaymentFeeRequest!) {
    getPaymentFee(payload: $payload) {
      profileFeeList {
          convenienceFee
          paymentAmount
          totalAmount
          token
        }
      }
  }
`;

export const useInvoicePayment = (invoiceId: string) => {
  const apolloClient = useApolloClient();
  const { encryptedPersonId } = useServiceProviderInfo();
  const { authUser } = useInvoicePaymentAuth();

  const {
    loading: invoiceLoading,
    data: invoiceData,
  } = useInvoicePaymentAuthQuery<{
    getInvoiceDetails: InvoiceDetailsList;
  }>(GET_INVOICE_LIST_QUERY, {
    variables: {
      params: {        
        billId: invoiceId,
      },
    },
    context: {
      invoicePayment: true,
    },
    
  });
  
  useEffect(() => {
    if (!invoiceLoading) {
      setInvoicePaymentState(state => ({
        ...state,
        invoiceDetails: invoiceData?.getInvoiceDetails?.billsList!?.[0] || null,
      }));
    }
  }, [invoiceData, invoiceLoading]);

  const { setErrorNotification } = useUtil();
  const { richT } = useTranslation();
  const { wrapWithLoader } = useWrapWithLoader();

  const form = useFormState({
    paymentPreference: '',
  });

  const [invoicePaymentState, setInvoicePaymentState] = useState<
    InvoicePaymentState
  >({
    paymentInfo: null,
    invoiceDetails: null,
    paymentConfirmation: undefined,
  });

  const savePaymentInfo = (paymentInfo: OneTimePayConfigData) => {
    setInvoicePaymentState(state => ({
      ...state,
      paymentInfo,
    }));
    void navigate(
      getInvoiceUrl(routes.INVOICE_PAYMENT_PORTAL_INVOICE_PAYMENT_VERIFY,invoiceId));        
  };

  
  const submitPayment = wrapWithLoader(async () => {
    const payload: InvoicePaymentInputList = {
      encryptedPersonId,
      cssUserEmail: authUser?.email,
      payment: [],
    };
    const { paymentInfo, invoiceDetails } = invoicePaymentState;
    paymentInfo?.paymentSelector.selectedPaymentProfileList.map(p => {
      payload.payment.push({
        encryptedPersonId: encryptedPersonId!,
        invoiceId: invoiceId,
        tokenId: p.profile.token,
        projectAddress: invoiceDetails?.projectAddress,
        projectNum: invoiceDetails?.projectNum,
        paymentAmount:
          paymentInfo?.paymentSelector.selectedPaymentProfileList.length === 2
            ? Number(Number(p.value).toFixed(2))
            : Number(Number(paymentInfo?.paymentAmount).toFixed(2)),
        paymentDate: paymentInfo.paymentDate,
      });
    });
    try {
      const response = await apolloClient.mutate<
        {
          invoiceMakePayment: InvoicePaymentConfirmationList;
        },
        MutationInvoiceMakePaymentArgs
      >({
        mutation: submitPaymentMutation,
        variables: { payload },
        errorPolicy: 'all',
        context: { invoicePayment: true },
      });

      if (response?.data?.invoiceMakePayment?.paymentConfirmation.length) {
        await navigate(
          getInvoiceUrl(
            routes.INVOICE_PAYMENT_PORTAL_INVOICE_PAYMENT_CONFIRMATION,
            invoiceId,
          ),
          {
            state: {
              oneTimePayConfirmationData: response?.data?.invoiceMakePayment,
              oneTimePayConfig: invoicePaymentState?.paymentInfo,
              paymentMethodCapLimit: 2,
              invoiceId,
            },
          },
        );
      } else {
        setErrorNotification(true, richT('PAYMENT_ERROR_MESSAGE'));
      }
    } catch (_) {
      setErrorNotification(true, richT('PAYMENT_ERROR_MESSAGE'));
    }
  });

  return {
    form,
    invoiceLoading,
    invoice: invoiceData ? invoiceData?.getInvoiceDetails.billsList![0] : null,
    invoicePaymentState,
    savePaymentInfo,
    submitPayment,    
  };
};
